import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Flame, Target, Zap, Activity, RefreshCw } from 'lucide-react';
import { isMarketClosed } from '../../utils/marketCalendar';
import apiService from '../../services/apiService';

const Container = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  color: #e5e5e5;
  padding: 20px;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #222;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  letter-spacing: -0.5px;
  span { color: #e50914; }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s;
  &:hover { color: #fff; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  @media (min-width: 1200px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const Card = styled.div`
  background: #141414;
  border: 1px solid #222;
  border-radius: 16px;
  padding: 24px;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
`;

const LeaderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th { text-align: left; color: #666; padding: 12px 8px; border-bottom: 1px solid #222; }
  td { padding: 16px 8px; border-bottom: 1px solid #1a1a1a; }
`;

const GradeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 800;
  font-size: 12px;
  background: ${props => props.$grade === 'S' ? '#e50914' : props.$grade === 'A' ? '#f59e0b' : props.$grade === 'B' ? '#3b82f6' : '#333'};
  color: #fff;
`;

const PivotCard = styled.div`
  background: #1a1a1a;
  border: 1px solid ${props => props.$status === 'BREAKOUT' ? 'rgba(229, 9, 20, 0.5)' : '#333'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusTag = styled.span`
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$status === 'BREAKOUT' ? '#e50914' : props.$status === 'NEAR_BREAKOUT' ? '#f59e0b' : '#333'};
  color: #fff;
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #666;
`;

const RefreshButton = styled.button`
  background: none;
  border: 1px solid #333;
  color: #888;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { color: #fff; border-color: #e50914; background: rgba(229, 9, 20, 0.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export default function LivermoreUIPage() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState('');

    useEffect(() => {
        runClientSideAnalysis();
    }, []);

    const getFormattedDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    };

    const runClientSideAnalysis = async () => {
        setLoading(true);
        try {
            setProgress('백엔드에서 기초 설정 불러오는 중...');
            const configReq = await apiService.request('/stock/kis-config');
            const { appKey, appSecret, baseUrl } = configReq;

            setProgress('KIS API 인증 토큰 발급 중 (직접 연결)...');
            const tokenRes = await fetch(`${baseUrl}/oauth2/tokenP`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ grant_type: 'client_credentials', appkey: appKey, appsecret: appSecret })
            }).then(r => r.json());
            
            const token = tokenRes.access_token;
            if(!token) throw new Error('KIS Token 발급 실패 (CORS 또는 권한 에러일 수 있습니다)');

            setProgress('네이버 뉴스 및 시가총액 데이터 백엔드 프록시 호출 중...');
            const helpers = await apiService.request('/livermore/ui-helpers');
            const { news, stockCodes } = helpers;
            const mcaps = {};
            stockCodes.forEach(c => mcaps[c.code] = c);

            setProgress('KIS에서 거래대금 상위 종목 다운로드 중...');
            const volumeHeaders = {
                'authorization': `Bearer ${token}`,
                'appkey': appKey,
                'appsecret': appSecret,
                'tr_id': 'FHPST01710000',
                'custtype': 'P'
            };
            
            // Fetch KOSPI + KOSDAQ
            let volList = [];
            for (const mkt of ['J', 'W']) {
                const volUrl = `${baseUrl}/uapi/domestic-stock/v1/quotations/psearch-title?FID_COND_MRKT_DIV_CODE=${mkt}&FID_COND_SCR_DIV_CODE=20171&FID_INPUT_ISCD=0000&FID_DIV_CLS_CODE=0&FID_BLNG_CLS_CODE=0&FID_TRGT_CLS_CODE=111111111&FID_TRGT_EXLS_CLS_CODE=0000000000&FID_INPUT_PRICE_1=&FID_INPUT_PRICE_2=&FID_VOL_CNT=&FID_INPUT_DATE_1=`;
                const res = await fetch(volUrl, { headers: volumeHeaders }).then(r=>r.json());
                if (res?.output) {
                    volList = volList.concat(res.output);
                }
            }
            
            // Sort by trValue (acml_tr_pbmn)
            volList.sort((a, b) => parseInt(b.acml_tr_pbmn || 0) - parseInt(a.acml_tr_pbmn || 0));
            volList = volList.slice(0, 50);

            setProgress('리더스 계산 진행 중...');
            const leaders = [];
            for (const item of volList) {
                const code = item.mksc_shrn_iscd || item.stck_shrn_iscd;
                const name = item.hts_kor_isnm;
                const price = parseInt(item.stck_prpr);
                const trValue = parseInt(item.acml_tr_pbmn);
                
                let newsScore = 0;
                news.forEach(title => { if (title.includes(name)) newsScore += 10; });
                if (newsScore > 40) newsScore = 40;

                const mcap = mcaps[code]?.marketCap || 5000;
                const trRatio = trValue / (mcap * 100000000);
                const volScore = Math.min(trRatio * 1000, 30);
                
                const changeRate = parseFloat(item.prdy_ctrt || 0);
                const priceScore = Math.min(Math.max(changeRate * 2, 0), 30);
                const totalScore = newsScore + volScore + priceScore;
                
                let grade = 'C';
                if (totalScore >= 70) grade = 'S';
                else if (totalScore >= 50) grade = 'A';
                else if (totalScore >= 30) grade = 'B';

                leaders.push({ code, name, price, changeRate, trValue, mcap, trRatio, newsScore, volScore, priceScore, totalScore, grade });
            }
            leaders.sort((a, b) => b.totalScore - a.totalScore);

            setProgress('개별 종목 피벗 강도 분석 중 (개별 일봉 조회 중...)');
            const candidates = leaders.slice(0, 20);
            const pivotPoints = [];

            const today = new Date();
            const oneYearAgo = new Date(today);
            oneYearAgo.setFullYear(today.getFullYear() - 1);
            const strToday = getFormattedDate(today);
            const strStart = getFormattedDate(oneYearAgo);

            for (const stock of candidates) {
                try {
                    const candleHeaders = {
                        'authorization': `Bearer ${token}`,
                        'appkey': appKey,
                        'appsecret': appSecret,
                        'tr_id': 'FHKST03010100',
                        'custtype': 'P'
                    };
                    const candleUrl = `${baseUrl}/uapi/domestic-stock/v1/quotations/inquire-daily-itemchartprice?FID_COND_MRKT_DIV_CODE=J&FID_INPUT_ISCD=${stock.code}&FID_INPUT_DATE_1=${strStart}&FID_INPUT_DATE_2=${strToday}&FID_PERIOD_DIV_CODE=D&FID_ORG_ADJ_PRC=0`;
                    
                    const cRes = await fetch(candleUrl, { headers: candleHeaders }).then(r=>r.json());
                    if(!cRes.output2) continue;
                    
                    const candles = cRes.output2.map(item => ({
                        time: item.stck_bsop_date,
                        close: parseInt(item.stck_clpr),
                        high: parseInt(item.stck_hgpr),
                        low: parseInt(item.stck_lwpr),
                        volume: parseInt(item.acml_vol)
                    })).filter(c => !isNaN(c.close)).reverse();

                    if (candles.length < 15) continue;

                    const recent = candles.slice(-20);
                    const current = recent[recent.length - 1];
                    const previous = recent.slice(0, -1);

                    const prices = previous.map(c => c.high);
                    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
                    const maxPrice = Math.max(...prices);
                    const minPrice = Math.min(...prices);
                    const volatility = (maxPrice - minPrice) / avg;
                    
                    const isFlatBase = volatility < 0.12;
                    const avgVol = previous.reduce((a, b) => a + b.volume, 0) / previous.length;
                    const volSurge = current.volume / avgVol;
                    const isBreakout = current.close > maxPrice && volSurge > 1.8;
                    const isNearBreakout = !isBreakout && current.close > (maxPrice * 0.95) && isFlatBase;

                    if (isFlatBase || isBreakout || isNearBreakout) {
                        let status = 'FORMING_BASE';
                        if (isBreakout) status = 'BREAKOUT';
                        else if (isNearBreakout) status = 'NEAR_BREAKOUT';
                        
                        pivotPoints.push({
                            ...stock,
                            pivotStatus: status,
                            volSurge,
                            volatility: (volatility * 100).toFixed(2) + '%',
                            resistanceLine: maxPrice
                        });
                    }
                } catch(err) {
                    console.error('Pivot check error for', stock.name, err);
                }
                // Small delay to prevent rate limit purely from client
                await new Promise(r => setTimeout(r, 100));
            }

            setProgress('완료되었습니다!');
            setData({
                timestamp: Date.now(),
                leaders: leaders.slice(0, 30),
                pivotPoints,
                marketMood: leaders.filter(l => l.grade === 'S').length > 3 ? 'HOT' : 'NEUTRAL'
            });

        } catch (err) {
            console.error(err);
            alert("UI 직접 실행 중 오류 발생: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !data) {
        return (
            <Container>
                <Header>
                    <TitleSection>
                        <BackButton onClick={() => navigate('/labs')}>
                            <ArrowLeft size={20} />
                        </BackButton>
                        <Title>Livermore <span>UI-Direct</span></Title>
                    </TitleSection>
                </Header>
                <LoadingOverlay>
                    <Activity size={48} className="animate-pulse" />
                    <p style={{ marginTop: 20 }}>{progress || '휴대폰 네트워크를 통해 단독 계산 중...'}</p>
                </LoadingOverlay>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <TitleSection>
                    <BackButton onClick={() => navigate('/labs')}>
                        <ArrowLeft size={20} />
                    </BackButton>
                    <Title>Livermore <span>UI-Direct</span></Title>
                    <div style={{ color: '#ef4444', fontSize: '10px', fontWeight: 700, background: 'rgba(239, 68, 68, 0.1)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        Client Mode
                    </div>
                </TitleSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#555', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Local Calculation</div>
                        <div style={{ color: '#888', fontSize: '12px', fontWeight: 600 }}>
                            {new Date(data.timestamp).toLocaleTimeString()}
                        </div>
                    </div>
                    <RefreshButton onClick={runClientSideAnalysis} disabled={loading}>
                        <RefreshCw size={14} style={{ animation: loading ? 'spin 2s linear infinite' : 'none' }} />
                        Reload
                    </RefreshButton>
                </div>
            </Header>

            <Grid>
                <Card>
                    <CardTitle><Target size={20} color="#e50914" /> 주도주 스캐너 (Client Execution)</CardTitle>
                    <div style={{ overflowX: 'auto' }}>
                        <LeaderTable>
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Stock</th>
                                    <th>Price</th>
                                    <th>Change</th>
                                    <th>Volume (B)</th>
                                    <th>Grade</th>
                                    <th>Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.leaders.map((stock, i) => (
                                    <tr key={stock.code}>
                                        <td style={{ color: '#888', fontWeight: 600, fontSize: '12px' }}>#{i+1}</td>
                                        <td style={{ fontWeight: 600 }}>{stock.name}</td>
                                        <td>{stock.price.toLocaleString()}</td>
                                        <td style={{ color: stock.changeRate > 0 ? '#ef4444' : stock.changeRate < 0 ? '#3b82f6' : '#888' }}>
                                            {stock.changeRate > 0 ? '+' : ''}{stock.changeRate}%
                                        </td>
                                        <td style={{ color: '#aaa', fontSize: '12px' }}>
                                            {(stock.trValue / 100000000).toFixed(1)}억
                                        </td>
                                        <td><GradeBadge $grade={stock.grade}>{stock.grade}</GradeBadge></td>
                                        <td style={{ color: '#f59e0b', fontWeight: 700 }}>{stock.totalScore.toFixed(0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </LeaderTable>
                    </div>
                </Card>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <Card>
                        <CardTitle><Flame size={20} color="#f59e0b" /> 피벗 포인트 포착</CardTitle>
                        {data.pivotPoints.length > 0 ? data.pivotPoints.map(pivot => (
                            <PivotCard key={pivot.code} $status={pivot.pivotStatus}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '8px', color: '#fff' }}>
                                        {pivot.name}
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#888' }}>
                                        <span>Vol: {(pivot.volSurge||0).toFixed(1)}x</span>
                                        <span>Max: {pivot.resistanceLine?.toLocaleString() || '-'}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                    <StatusTag $status={pivot.pivotStatus}>
                                        {pivot.pivotStatus.replace('_', ' ')}
                                    </StatusTag>
                                </div>
                            </PivotCard>
                        )) : (
                            <div style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                                현재 포착된 돌파 패턴이 없습니다.
                            </div>
                        )}
                    </Card>
                </div>
            </Grid>
        </Container>
    );
}
